import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, Button, useTheme } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

import withErrorHandling from "../HOC/ErrorHandling";
import { useUnifiedApps } from "../hooks/useUnifiedApps";
import { getBuilds } from "../helpers/utils";
import Loader from "../components/Loading";

const POLL_MS = 5000;

// Checkpoint order the design calls for: services -> build -> deploy -> ready.
const CHECKPOINTS = [
  { id: "services", label: "Services provisioned" },
  { id: "building", label: "Building image" },
  { id: "deploying", label: "Deploying" },
  { id: "ready", label: "Ready" },
];

function deriveActiveCheckpoint(build, runtimeUrl) {
  if (runtimeUrl) return "ready";
  if (!build) return "building";
  const status = build?.status?.conditions?.[0];
  if (!status) return "building";
  if (status.status === "True") return "deploying";
  if (status.status === "False") return "building"; // surfaced as failed via message below
  return "building";
}

const AppDeploying = (props) => {
  const { showError } = props;
  const { id: appId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Passed by TemplateProvisionDialog on success so the hero can render
  // instantly, before the apps list has caught up with the new app.
  const navState = location.state || {};
  const template = navState.template;
  const appName = navState.appName;

  const { apps } = useUnifiedApps({ showError });
  const app = useMemo(
    () => apps?.find((a) => a.id === appId || a?._raw?.build?.metadata?.uid === appId),
    [apps, appId]
  );

  const [builds, setBuilds] = useState(null);
  const [buildError, setBuildError] = useState(null);

  const imageName = app?.name;

  const pollBuilds = useCallback(() => {
    if (!imageName) return;
    getBuilds({ imageName }, (result, err) => {
      if (Array.isArray(result)) {
        setBuilds(
          [...result].sort(
            (a, b) => new Date(b.metadata.creationTimestamp) - new Date(a.metadata.creationTimestamp)
          )
        );
      } else if (err) {
        setBuildError(err);
      }
    });
  }, [imageName]);

  useEffect(() => {
    if (!imageName) return;
    pollBuilds();
    const interval = setInterval(pollBuilds, POLL_MS);
    return () => clearInterval(interval);
  }, [imageName, pollBuilds]);

  const latestBuild = builds?.[0];
  const runtimeUrl = app?.runtime?.url;
  const active = deriveActiveCheckpoint(latestBuild, runtimeUrl);
  const activeIndex = CHECKPOINTS.findIndex((c) => c.id === active);
  const buildFailed = latestBuild?.status?.conditions?.[0]?.status === "False";

  const displayName = app?.displayName || appName || "your app";
  const emoji = template?.emoji || "🚀";
  const templateName = template?.displayName || template?.display_name;

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", py: 6, px: 3 }}>
      {/* Hero */}
      <Box sx={{ textAlign: "center", mb: 5 }}>
        <Box sx={{ fontSize: "2.75rem", mb: 1 }}>{emoji}</Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {displayName}
        </Typography>
        {templateName && (
          <Typography variant="body2" sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", mt: 0.5 }}>
            Created from the {templateName} template
          </Typography>
        )}
      </Box>

      {/* Checkpoints */}
      <Box
        sx={{
          borderRadius: 3,
          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
          backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
          p: 3,
        }}
      >
        {CHECKPOINTS.map((cp, idx) => {
          const done = idx < activeIndex || (idx === activeIndex && active === "ready");
          const current = idx === activeIndex && active !== "ready";
          const failed = current && cp.id === "building" && buildFailed;

          return (
            <Box
              key={cp.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5,
                borderBottom: idx < CHECKPOINTS.length - 1 ? `1px solid ${isDarkMode ? "#334155" : "#f1f5f9"}` : "none",
              }}
            >
              {done ? (
                <CheckCircleIcon sx={{ color: "#10b981", fontSize: 22 }} />
              ) : current ? (
                failed ? (
                  <RadioButtonUncheckedIcon sx={{ color: "#ef4444", fontSize: 22 }} />
                ) : (
                  <Loader specificDetails={{ size: 20 }} />
                )
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: isDarkMode ? "#475569" : "#cbd5e1", fontSize: 22 }} />
              )}
              <Typography
                sx={{
                  fontWeight: done || current ? 600 : 400,
                  color: failed
                    ? "#ef4444"
                    : done || current
                    ? isDarkMode
                      ? "#f1f5f9"
                      : "#1e293b"
                    : isDarkMode
                    ? "#64748b"
                    : "#94a3b8",
                }}
              >
                {cp.label}
                {failed && " — build failed"}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {buildError && (
        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#ef4444" }}>
          Couldn't check build status: {String(buildError)}
        </Typography>
      )}

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
        <Button
          size="small"
          onClick={() => navigate("/")}
          sx={{ textTransform: "none", color: isDarkMode ? "#94a3b8" : "#64748b" }}
        >
          View build logs
        </Button>

        {runtimeUrl && (
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            href={runtimeUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          >
            Open app
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default withErrorHandling(AppDeploying);
